import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ArrowUpRight, Check, Sparkles, BookOpen, Crown, Feather } from "lucide-react";
import { publishingApi, paymentsApi } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { PublishingNav, PublishingFooter } from "@/components/publishing/PublishingShared";
import { BusinessInquiryModal } from "@/components/publishing/BusinessInquiryModal";

// Hero Section
const BooksHero = () => (
  <section 
    data-testid="books-hero"
    className="relative min-h-[95vh] flex items-center overflow-hidden bg-primary"
  >
    {/* Ambient gradient background */}
    <div className="absolute inset-0">
      <div className="absolute top-1/3 left-1/4 w-[600px] h-[600px] rounded-full bg-jcrown-gold/5 blur-3xl" />
      <div className="absolute bottom-1/3 right-1/4 w-[500px] h-[500px] rounded-full bg-jcrown-purple/10 blur-3xl" />
    </div>

    <div className="relative z-10 max-w-7xl mx-auto px-6 py-32 w-full">
      <div className="max-w-4xl">
        <div className="flex items-center gap-3 mb-8 animate-fade-in">
          <div className="h-px w-8 bg-jcrown-gold/60" />
          <p className="text-jcrown-gold/80 text-[10px] tracking-[0.4em] uppercase font-body">
            Goodman Entertainment Presents
          </p>
        </div>
        
        <h1 className="font-heading text-white text-5xl sm:text-7xl md:text-8xl lg:text-[9rem] leading-[0.85] tracking-tighter uppercase animate-slide-up">
          THE JOHNNY
          <br />
          <span className="text-jcrown-gold">CROWN</span>
          <br />
          <span className="text-white/30">LIBRARY</span>
        </h1>
        
        <p className="mt-10 text-white/70 font-body text-xl md:text-2xl max-w-2xl leading-tight animate-fade-in" style={{ animationDelay: '0.3s' }}>
          Six books. Six worlds. One imagination.
        </p>
        
        <p className="mt-6 text-white/40 font-body text-base md:text-lg max-w-2xl leading-relaxed animate-fade-in" style={{ animationDelay: '0.5s' }}>
          Discover the original literary worlds of Johnny Crown — from cosmic heroes and imaginative adventures to unforgettable characters created for readers of all ages.
        </p>
        
        <div className="mt-12 flex flex-wrap gap-4 animate-fade-in" style={{ animationDelay: '0.7s' }}>
          <a 
            href="#collection"
            data-testid="explore-collection-btn"
            className="group bg-jcrown-gold text-jcrown-dark px-8 py-4 font-body text-sm tracking-widest uppercase hover:bg-white hover:text-black transition-all active:scale-95 inline-flex items-center gap-2"
          >
            Explore the Collection
            <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
          </a>
          <a 
            href="#complete-bundle"
            data-testid="get-complete-btn"
            className="group border border-white/20 text-white px-8 py-4 font-body text-sm tracking-widest uppercase hover:border-white hover:bg-white/5 transition-all active:scale-95 inline-flex items-center gap-2"
          >
            Get the Complete Collection
          </a>
        </div>
      </div>
    </div>

    <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2">
      <span className="text-white/20 text-[10px] tracking-[0.3em] uppercase font-body">Discover</span>
      <div className="w-px h-16 bg-gradient-to-b from-white/30 to-transparent animate-pulse" />
    </div>
  </section>
);

// Book Card
const BookCard = ({ book, index }) => {
  const [aspect, setAspect] = useState(null);
  return (
    <Link 
      to={`/books/${book.slug}`}
      data-testid={`book-card-${book.slug}`}
      className="group relative flex flex-col"
      style={{ animationDelay: `${index * 0.1}s` }}
    >
      {/* Cover container - preserves natural aspect ratio */}
      <div 
        className="relative overflow-hidden bg-black/40 mb-6 transition-all duration-500 group-hover:-translate-y-1 flex items-center justify-center"
        style={aspect ? { aspectRatio: aspect } : { aspectRatio: '2 / 3' }}
      >
        <img 
          src={book.cover_image}
          alt={`${book.title_display} cover`}
          loading="lazy"
          onLoad={(e) => setAspect(`${e.target.naturalWidth} / ${e.target.naturalHeight}`)}
          className="w-full h-full object-contain transition-transform duration-700 group-hover:scale-[1.03]"
        />
        {/* Subtle accent glow */}
        <div 
          className="absolute inset-0 pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-500"
          style={{ boxShadow: `inset 0 0 0 1px ${book.accent_color}80, 0 20px 60px -20px ${book.accent_color}40` }}
        />
        {/* Explore reveal */}
        <div className="absolute inset-x-0 bottom-0 p-6 opacity-0 group-hover:opacity-100 transition-all duration-500 translate-y-4 group-hover:translate-y-0">
          <div className="bg-white text-black px-4 py-3 font-body text-xs tracking-widest uppercase inline-flex items-center gap-2">
            Explore Book
            <ArrowUpRight className="w-3.5 h-3.5" />
          </div>
        </div>
      </div>
      
      {/* Book info */}
      <div>
        <p className="text-white/40 text-[10px] tracking-[0.3em] uppercase font-body mb-2">
          {book.category}
        </p>
        <h3 className="font-heading text-white text-2xl md:text-3xl tracking-tight uppercase mb-1 group-hover:text-jcrown-gold transition-colors duration-300">
          {book.title_display}
        </h3>
        <p className="text-white/50 font-body text-sm">
          {book.author_credit}
        </p>
        {book.illustrator && (
          <p className="text-white/30 font-body text-xs mt-0.5">
            Illustrated by {book.illustrator}
          </p>
        )}
      </div>
    </Link>
  );
};

// Book Gallery
const BookGallery = ({ books }) => (
  <section id="collection" data-testid="book-gallery" className="relative bg-primary py-24 md:py-32">
    <div className="max-w-7xl mx-auto px-6">
      <div className="mb-16">
        <p className="text-jcrown-gold/60 text-[10px] tracking-[0.4em] uppercase font-body mb-4">
          The Collection
        </p>
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
          <h2 className="font-heading text-white text-4xl md:text-6xl lg:text-7xl tracking-tighter uppercase">
            SIX BOOKS.
            <br />
            <span className="text-white/30">SIX WORLDS.</span>
          </h2>
          <p className="text-white/40 font-body text-sm md:text-base max-w-md">
            Each title stands alone as an original creative universe. Together they form the foundation of the Johnny Crown Library.
          </p>
        </div>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-12 md:gap-10">
        {books.map((book, i) => (
          <BookCard key={book.id} book={book} index={i} />
        ))}
      </div>
    </div>
  </section>
);

// Complete Collection / Pricing Section
const CollectionSection = ({ pricing }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [purchasing, setPurchasing] = useState(null);

  const buy = async (lookup_key) => {
    if (!user) {
      navigate(`/register?redirect=${encodeURIComponent(window.location.pathname + '#complete-bundle')}`);
      return;
    }
    setPurchasing(lookup_key);
    try {
      const { checkout_url } = await paymentsApi.checkout(lookup_key);
      window.location.href = checkout_url;
    } catch (e) {
      setPurchasing(null);
      alert("Checkout failed. Please try again.");
    }
  };

  return (
  <section id="complete-bundle" data-testid="collection-section" className="relative bg-jcrown-dark py-24 md:py-32 overflow-hidden">
    <div className="absolute inset-0 opacity-40 pointer-events-none">
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[900px] h-[900px] rounded-full bg-gradient-radial from-jcrown-gold/10 via-transparent to-transparent blur-3xl" />
    </div>

    <div className="relative z-10 max-w-7xl mx-auto px-6">
      <div className="text-center mb-16">
        <p className="text-jcrown-gold/70 text-[10px] tracking-[0.4em] uppercase font-body mb-4">
          Access the Library
        </p>
        <h2 className="font-heading text-white text-4xl md:text-6xl tracking-tighter uppercase">
          Own the Universe
        </h2>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
        {/* Individual */}
        <div data-testid="pricing-individual" className="relative border border-white/10 bg-black/40 p-8 md:p-10 flex flex-col">
          <p className="text-white/40 text-[10px] tracking-[0.3em] uppercase font-body mb-4">Individual</p>
          <h3 className="font-heading text-white text-3xl tracking-tight uppercase mb-2">Single Book</h3>
          <p className="text-white/40 font-body text-sm mb-8">Own one digital edition forever.</p>
          <div className="mb-8">
            <span className="font-heading text-white text-5xl tracking-tight">${pricing.individual_book}</span>
            <span className="text-white/40 font-body text-sm ml-2">/ book</span>
          </div>
          <ul className="space-y-3 mb-8 flex-1">
            <li className="flex items-start gap-3 text-white/60 font-body text-sm">
              <Check className="w-4 h-4 text-jcrown-gold shrink-0 mt-0.5" />
              Permanent access
            </li>
            <li className="flex items-start gap-3 text-white/60 font-body text-sm">
              <Check className="w-4 h-4 text-jcrown-gold shrink-0 mt-0.5" />
              Read on any device
            </li>
            <li className="flex items-start gap-3 text-white/60 font-body text-sm">
              <Check className="w-4 h-4 text-jcrown-gold shrink-0 mt-0.5" />
              Free preview available
            </li>
          </ul>
          <a 
            href="#collection"
            data-testid="individual-cta"
            className="w-full text-center border border-white/20 text-white px-6 py-3 font-body text-xs tracking-widest uppercase hover:border-white hover:bg-white/5 transition-all active:scale-95 inline-block"
          >
            Browse Books
          </a>
        </div>

        {/* Complete Collection - Featured */}
        <div data-testid="pricing-collection" className="relative border-2 border-jcrown-gold bg-black/60 p-8 md:p-10 flex flex-col md:scale-105 md:-my-2">
          <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-jcrown-gold text-jcrown-dark px-4 py-1 text-[10px] tracking-widest uppercase font-body">
            Best Value
          </div>
          <p className="text-jcrown-gold text-[10px] tracking-[0.3em] uppercase font-body mb-4">Complete Collection</p>
          <h3 className="font-heading text-white text-3xl tracking-tight uppercase mb-2">All Six Books</h3>
          <p className="text-white/40 font-body text-sm mb-8">The entire Johnny Crown Library.</p>
          <div className="mb-2">
            <span className="text-white/30 font-body text-lg line-through mr-3">${pricing.collection_regular_value}</span>
            <span className="text-jcrown-gold/70 text-xs tracking-widest uppercase font-body">Save ${pricing.collection_savings}</span>
          </div>
          <div className="mb-8">
            <span className="font-heading text-white text-5xl tracking-tight">${pricing.complete_collection}</span>
            <span className="text-white/40 font-body text-sm ml-2">once</span>
          </div>
          <ul className="space-y-3 mb-8 flex-1">
            <li className="flex items-start gap-3 text-white/70 font-body text-sm">
              <Check className="w-4 h-4 text-jcrown-gold shrink-0 mt-0.5" />
              All 6 books unlocked forever
            </li>
            <li className="flex items-start gap-3 text-white/70 font-body text-sm">
              <Check className="w-4 h-4 text-jcrown-gold shrink-0 mt-0.5" />
              Save 30% vs individual
            </li>
            <li className="flex items-start gap-3 text-white/70 font-body text-sm">
              <Check className="w-4 h-4 text-jcrown-gold shrink-0 mt-0.5" />
              Read on any device
            </li>
            <li className="flex items-start gap-3 text-white/70 font-body text-sm">
              <Check className="w-4 h-4 text-jcrown-gold shrink-0 mt-0.5" />
              Yours to keep
            </li>
          </ul>
          <button 
            onClick={() => buy("complete_collection")}
            disabled={purchasing === "complete_collection"}
            data-testid="collection-cta"
            className="w-full bg-jcrown-gold text-jcrown-dark px-6 py-3 font-body text-xs tracking-widest uppercase hover:bg-white hover:text-black transition-all active:scale-95 disabled:opacity-50"
          >
            {purchasing === "complete_collection" ? "Redirecting..." : "Get the Collection"}
          </button>
        </div>

        {/* Library Pass Subscription */}
        <div id="pass" data-testid="pricing-pass" className="relative border border-white/10 bg-black/40 p-8 md:p-10 flex flex-col">
          <p className="text-white/40 text-[10px] tracking-[0.3em] uppercase font-body mb-4">Membership</p>
          <h3 className="font-heading text-white text-3xl tracking-tight uppercase mb-2">Library Pass</h3>
          <p className="text-white/40 font-body text-sm mb-8">Access the growing library.</p>
          <div className="mb-8">
            <span className="font-heading text-white text-5xl tracking-tight">${pricing.library_pass_monthly}</span>
            <span className="text-white/40 font-body text-sm ml-2">/ month</span>
          </div>
          <ul className="space-y-3 mb-8 flex-1">
            <li className="flex items-start gap-3 text-white/60 font-body text-sm">
              <Check className="w-4 h-4 text-jcrown-gold shrink-0 mt-0.5" />
              All current library books
            </li>
            <li className="flex items-start gap-3 text-white/60 font-body text-sm">
              <Check className="w-4 h-4 text-jcrown-gold shrink-0 mt-0.5" />
              All future releases included
            </li>
            <li className="flex items-start gap-3 text-white/60 font-body text-sm">
              <Check className="w-4 h-4 text-jcrown-gold shrink-0 mt-0.5" />
              Cancel anytime
            </li>
          </ul>
          <button 
            onClick={() => buy("library_pass_monthly")}
            disabled={purchasing === "library_pass_monthly"}
            data-testid="pass-cta"
            className="w-full border border-white/20 text-white px-6 py-3 font-body text-xs tracking-widest uppercase hover:border-white hover:bg-white/5 transition-all active:scale-95 disabled:opacity-50"
          >
            {purchasing === "library_pass_monthly" ? "Redirecting..." : "Join the Library"}
          </button>
        </div>
      </div>

      <p className="text-center text-white/30 font-body text-xs tracking-widest uppercase mt-12">
        Secure checkout by Stripe • Test mode • Card 4242 4242 4242 4242
      </p>
    </div>
  </section>
  );
};

// Original Worlds / IP Section
const WorldsSection = ({ books }) => (
  <section data-testid="worlds-section" className="relative bg-primary py-24 md:py-32">
    <div className="max-w-7xl mx-auto px-6">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
        <div className="lg:col-span-5">
          <p className="text-jcrown-gold/60 text-[10px] tracking-[0.4em] uppercase font-body mb-4">
            Original Intellectual Property
          </p>
          <h2 className="font-heading text-white text-4xl md:text-5xl lg:text-6xl tracking-tighter uppercase mb-8 leading-[0.9]">
            SIX BOOKS.
            <br />
            <span className="text-jcrown-gold">SIX WORLDS.</span>
          </h2>
          <p className="text-white/50 font-body text-base md:text-lg leading-relaxed mb-6">
            From the streets of Fort Greene to distant galaxies, fantastical creatures and extraordinary characters, the Johnny Crown Library represents a growing universe of original stories and intellectual property from Goodman Entertainment.
          </p>
          
          <div className="mt-10 grid grid-cols-2 gap-4">
            {["Animation", "Film & TV", "Merchandise", "Games", "Licensing", "Collectibles"].map((label) => (
              <div key={label} className="border border-white/10 p-4 hover:border-jcrown-gold/40 transition-colors">
                <p className="text-white/70 font-body text-sm tracking-wide">{label}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="lg:col-span-7 grid grid-cols-3 gap-4">
          {books.map((book) => {
            return (
              <Link 
                key={book.id}
                to={`/books/${book.slug}`}
                className="group relative overflow-hidden bg-black/40 flex items-center justify-center"
                style={{ aspectRatio: '2 / 3' }}
              >
                <img 
                  src={book.cover_image}
                  alt={book.title_display}
                  loading="lazy"
                  className="w-full h-full object-contain transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-4">
                  <p className="text-white font-heading text-sm tracking-tight uppercase">{book.title_display}</p>
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </div>
  </section>
);

// About Johnny Crown Section
const AboutCreator = () => (
  <section data-testid="about-creator" className="relative bg-secondary py-24 md:py-32">
    <div className="max-w-6xl mx-auto px-6">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
        {/* Portrait placeholder */}
        <div className="relative aspect-[4/5] bg-jcrown-dark overflow-hidden border border-jcrown-gold/20">
          <img 
            src="https://customer-assets-agu9un31.emergentagent.net/job_goodman-health-check/artifacts/7mlkbqe0_KING%20_COVER.PNG"
            alt="Johnny Crown"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent" />
          <div className="absolute bottom-6 left-6">
            <p className="text-jcrown-gold/70 text-[10px] tracking-[0.3em] uppercase font-body mb-1">
              The Creator
            </p>
            <p className="font-heading text-white text-2xl tracking-tight uppercase">
              Johnny Crown
            </p>
          </div>
        </div>

        <div>
          <p className="text-jcrown-gold/60 text-[10px] tracking-[0.4em] uppercase font-body mb-4">
            About the Creator
          </p>
          <h2 className="font-heading text-white text-4xl md:text-5xl tracking-tighter uppercase mb-8 leading-[0.9]">
            JOHNNY CROWN
          </h2>
          <p className="text-white/60 font-body text-base md:text-lg leading-relaxed mb-8">
            Johnny Crown is a multidisciplinary creator, recording artist, entrepreneur and storyteller whose work spans music, entertainment, fashion, original characters and publishing. Through Goodman Entertainment, Crown develops original worlds and intellectual property designed to entertain, inspire and connect with new generations.
          </p>
          <Link 
            to="/"
            data-testid="discover-goodman-btn"
            className="inline-flex items-center gap-2 border border-jcrown-gold/50 text-jcrown-gold px-8 py-4 font-body text-xs tracking-widest uppercase hover:bg-jcrown-gold hover:text-jcrown-dark transition-all active:scale-95"
          >
            Discover Goodman Entertainment
            <ArrowUpRight className="w-3.5 h-3.5" />
          </Link>
        </div>
      </div>
    </div>
  </section>
);

// Coming Soon email signup
const ComingSoonSection = () => {
  const [email, setEmail] = useState("");
  const [state, setState] = useState({ loading: false, success: false, error: "" });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setState({ loading: true, success: false, error: "" });
    try {
      const res = await publishingApi.signupEmail(email);
      setState({ loading: false, success: true, error: "" });
      setEmail("");
    } catch (err) {
      setState({ loading: false, success: false, error: "Please enter a valid email." });
    }
  };

  return (
    <section data-testid="coming-soon" className="relative bg-primary py-24 md:py-32 overflow-hidden">
      <div className="absolute inset-0 opacity-20 pointer-events-none">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[700px] rounded-full bg-jcrown-gold/10 blur-3xl" />
      </div>

      <div className="relative z-10 max-w-3xl mx-auto px-6 text-center">
        <Sparkles className="w-6 h-6 text-jcrown-gold/70 mx-auto mb-6" />
        <p className="text-jcrown-gold/70 text-[10px] tracking-[0.4em] uppercase font-body mb-4">
          Coming Soon
        </p>
        <h2 className="font-heading text-white text-4xl md:text-6xl tracking-tighter uppercase mb-6 leading-[0.9]">
          The Library
          <br />
          <span className="text-white/30">Is Growing</span>
        </h2>
        <p className="text-white/50 font-body text-base md:text-lg leading-relaxed mb-10 max-w-xl mx-auto">
          New stories and original worlds from Johnny Crown are coming soon. Be first to discover what's next.
        </p>

        <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
          <input 
            type="email"
            required
            placeholder="Your email"
            data-testid="signup-email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="flex-1 bg-black/40 border border-white/10 focus:border-jcrown-gold/50 text-white px-5 py-4 font-body text-sm outline-none transition-colors placeholder:text-white/30"
          />
          <button 
            type="submit"
            disabled={state.loading}
            data-testid="signup-submit"
            className="bg-jcrown-gold text-jcrown-dark px-8 py-4 font-body text-xs tracking-widest uppercase hover:bg-white hover:text-black transition-all active:scale-95 disabled:opacity-50"
          >
            {state.loading ? "..." : "Join the List"}
          </button>
        </form>
        {state.success && (
          <p className="mt-4 text-jcrown-gold/80 font-body text-sm">Welcome to the list.</p>
        )}
        {state.error && (
          <p className="mt-4 text-red-400 font-body text-sm">{state.error}</p>
        )}
      </div>
    </section>
  );
};

// Main Books Page
export default function Books() {
  const [books, setBooks] = useState([]);
  const [pricing, setPricing] = useState(null);
  const [inquiryOpen, setInquiryOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    if (!location.hash) window.scrollTo(0, 0);
    publishingApi.listBooks().then(data => {
      setBooks(data.books);
      setPricing(data.pricing);
    }).catch(() => {});
  }, [location.hash]);

  // After content mounts, scroll to hash target
  useEffect(() => {
    if (!pricing || !location.hash) return;
    const targetId = location.hash.replace('#', '');
    const el = document.getElementById(targetId);
    if (el) {
      requestAnimationFrame(() => {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    }
  }, [pricing, location.hash]);

  return (
    <div className="bg-primary min-h-screen">
      <div className="noise-overlay" />
      <PublishingNav />
      <BooksHero />
      {books.length > 0 && <BookGallery books={books} />}
      {pricing && <CollectionSection pricing={pricing} />}
      {books.length > 0 && <WorldsSection books={books} />}
      <AboutCreator />
      <ComingSoonSection />
      <PublishingFooter onBusinessInquiryClick={() => setInquiryOpen(true)} />
      <BusinessInquiryModal isOpen={inquiryOpen} onClose={() => setInquiryOpen(false)} />
    </div>
  );
}
